import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  VStack,
  HStack,
  Icon,
  Pressable,
  Text,
  Popover,
  PopoverBackdrop,
  PopoverContent,
  PopoverBody,
  Divider,
} from '@gluestack-ui/themed';
import { Mail, Users, ChevronRight } from 'lucide-react-native';
import { composeEmail, generateSlotEmailSubject } from '../utils/emailUtils';
import { useTenantEventKinds } from '../../../common/context/TenantSettingsStore';
import { useEventCalendarData } from '../hooks/useEventCalendarData';
import dayjs from 'dayjs';
import log from '../../../common/utils/logger';

/**
 * EmailStudentsMenu Component - Dropdown menu for emailing students registered in a slot
 */
const EmailStudentsMenu = ({ slot, activeBookings, activeBookingsByTask }) => {
  const { t } = useTranslation();
  const { getEventKindById } = useTenantEventKinds();
  const { taskMap, moduleMap } = useEventCalendarData();
  const [isOpen, setIsOpen] = React.useState(false);

  if (!activeBookings || activeBookings.length === 0) {
    return null;
  }

  const eventKind = getEventKindById(slot.eventKindId);
  const eventKindLabel = eventKind?.label || 'Session';

  const handleEmailAll = () => {
    const emails = activeBookings.map((b) => b.studentEmail).filter(Boolean);

    if (emails.length === 0) {
      log.warn('No student emails available');
      return;
    }

    const allModules = [...new Set(activeBookings.map((b) => b.moduleId).filter(Boolean))];
    const moduleTitle =
      allModules.length === 1
        ? moduleMap[allModules[0]] || allModules[0]
        : t('lecturer.eventCalendar.email.multipleModules');

    const subject = generateSlotEmailSubject(slot, eventKindLabel, moduleTitle, []);

    composeEmail({
      bcc: emails,
      subject,
      body: `${t('lecturer.eventCalendar.email.greeting')}\n\n${eventKindLabel}\n${dayjs(slot.startTime).format(
        'dddd, D MMMM YYYY',
      )}\n${dayjs(slot.startTime).format('HH:mm')} - ${dayjs(slot.endTime).format('HH:mm')}\n\n`,
    });

    setIsOpen(false);
  };

  const handleEmailByTask = (taskId) => {
    const taskBookings = activeBookingsByTask[taskId] || [];
    const emails = taskBookings.map((b) => b.studentEmail).filter(Boolean);

    if (emails.length === 0) {
      log.warn('No student emails available for this task');
      return;
    }

    const task = taskMap[taskId];
    const moduleId = taskBookings[0]?.moduleId;
    const moduleTitle = moduleId ? moduleMap[moduleId] || moduleId : '';
    const taskTitle = task?.title || taskId;

    const subject = generateSlotEmailSubject(slot, eventKindLabel, moduleTitle, [taskTitle]);

    composeEmail({
      bcc: emails,
      subject,
      body: `${t('lecturer.eventCalendar.email.greeting')}\n\n${eventKindLabel}\n${moduleTitle} - ${taskTitle}\n${dayjs(
        slot.startTime,
      ).format('dddd, D MMMM YYYY')}\n${dayjs(slot.startTime).format('HH:mm')} - ${dayjs(slot.endTime).format(
        'HH:mm',
      )}\n\n`,
    });

    setIsOpen(false);
  };

  const taskGroups = Object.entries(activeBookingsByTask);
  const hasMultipleTasks = taskGroups.length > 1;

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
      placement="bottom right"
      trigger={(triggerProps) => (
        <Pressable {...triggerProps} accessibilityLabel={t('lecturer.eventCalendar.email.menuLabel')}>
          <Icon as={Mail} size="xs" color="$textLight600" />
        </Pressable>
      )}
    >
      <PopoverBackdrop />
      <PopoverContent minWidth={220}>
        <PopoverBody>
          <VStack space="xs">
            <Pressable
              onPress={handleEmailAll}
              py="$2"
              px="$1"
              borderRadius="$sm"
              _hover={{ bg: '$backgroundLight100' }}
            >
              <HStack alignItems="center" space="sm">
                <Icon as={Users} size="xs" color="$textLight600" />
                <Text size="sm" flex={1}>
                  {t('lecturer.eventCalendar.email.emailAll', { count: activeBookings.length })}
                </Text>
              </HStack>
            </Pressable>

            {hasMultipleTasks && (
              <>
                <Divider my="$1" />
                {taskGroups.map(([taskId, bookings]) => {
                  const task = taskMap[taskId];
                  const taskTitle = task?.title || taskId;
                  const moduleId = bookings[0]?.moduleId;
                  const moduleTitle = moduleId ? moduleMap[moduleId] || moduleId : '';
                  const displayLabel =
                    moduleTitle && taskTitle !== 'unassigned'
                      ? `${moduleTitle} / ${taskTitle}`
                      : taskTitle !== 'unassigned'
                      ? taskTitle
                      : t('lecturer.eventCalendar.email.unassigned');

                  return (
                    <Pressable
                      key={taskId}
                      onPress={() => handleEmailByTask(taskId)}
                      py="$2"
                      px="$1"
                      borderRadius="$sm"
                      _hover={{ bg: '$backgroundLight100' }}
                    >
                      <HStack alignItems="center" space="sm">
                        <Icon as={ChevronRight} size="xs" color="$textLight400" />
                        <VStack flex={1}>
                          <Text size="xs" numberOfLines={2}>
                            {displayLabel}
                          </Text>
                          <Text size="2xs" color="$textLight500">
                            {bookings.length} {bookings.length === 1 ? 'student' : 'students'}
                          </Text>
                        </VStack>
                      </HStack>
                    </Pressable>
                  );
                })}
              </>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default EmailStudentsMenu;
